import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import appDisplay from 'scoreboard/components/app-display'
import formHelper from 'scoreboard/components/form-helper'
import dropdown from 'scoreboard/components/dropdown'
import config from 'scoreboard/components/config'
import ConfirmPrompt from 'scoreboard/components/confirm-prompt'
import highlight from 'scoreboard/components/highlight'
import i18n from 'scoreboard/components/i18n'
import emailValidator from 'scoreboard/components/validators/email-validator'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import MarkedField from 'scoreboard/components/marked-field'
import constants from 'scoreboard/components/constants'
import selects from 'scoreboard/components/selects'
import user from 'scoreboard/components/user'
import permissions from 'scoreboard/components/permissions'
import passwordValidation from 'scoreboard/components/password-validation'
import errorHandler from 'scoreboard/components/error-handler'
import template from './user-basics-form.hbs'

/**
 * Form elements and behavior common to Admin | Account Info and Admin | Users
 */
var UserBasicsForm = Backbone.View.extend({

	// this.model is a User

	className: 'user-basics-form',

	events: {
		'click .impersonate': '_onImpersonate'
	},

	/**
	 * {Boolean} [this.options.impersonation=false] pass true to enable
	 * "Login as" functionality
	 * {Boolean} [this.options.hideId] true to hide the user id in the panel
	 * header
	 * {Boolean} [this.options.isPanel=false] true if this form is in a panel
	 */
	initialize: function(options = {}) {
		this.options = options
		this.model.on('sync', function() {
			let label = this.model.get('label')
			let id = this.model.get('id')
			let idPrefix = i18n.get('common.id')
			this.$('.user-label').text(options.hideId ? label : `${label} (${idPrefix} ${id})`)
		}.bind(this))
		if (!this.model.isNew()) {
			this.setPasswordPlaceholders()
		}
	},

	render: function() {
		var context = _.defaults({}, this.model.attributes, {
			hasTitles: config.titles,
			label: i18n.get('admin.user.newUser'),
			impersonation: this.options.impersonation,
			userPermissionsAudit: permissions.modifyUsers()
		})
		this.$el.toggleClass('panel', this.options.isPanel === true)
		this.$el.empty().append(template(context))
		dropdown.values(this.$('select[name=title]'), config.titles)
		formHelper.writeToDom(this.model, this.el)
		if (this.model.id == constants.USER_ADMIN_ID) {
			this.$('label:not(.admin)').hide()
		}

		// setup simple required inputs
		var requiredFields = [ 'username', 'firstName', 'lastName']
		var requiredInputs = _.map(requiredFields, function(field) {
			return this.$('[name=' + field + ']')
		}.bind(this))
		this.markedFields = _.map(requiredInputs, function(required) {
			return new MarkedField({
				el: required,
				validator: requiredValidator
			}).render()
		})

		// set up some inputs that have special validators
		this.markedFields.push(new MarkedField({
			el: this.$('[name=email]'),
			validator: [requiredValidator, emailValidator]
		}).render())

		const passwordOptions = {required: config.userPasswordRequired}
		this.markedFields = this.markedFields.concat(passwordValidation.markFields(this, passwordOptions))

		selects(this.$('select'))

		// important: call after the call to create the selects
		this.secure()

		return this
	},

	secure: function() {

		if (!config.userPasswordVisible) {
			this.$('.user-profile-password, .user-profile-password-retype').remove()
		}

		if (this.model.isNew()) {
			// when creating a new user, all fields should be editable.  if a
			// user can't create users, they won't be able to make it here so
			// no security checks are required
			return
		}

		// when a user is editing themselves
		if (user.equals(this.model) && this.model.get('username') !== 'admin') {
			this._secureField('username')
			// just look at the modify personal account info permission
			if (!permissions.personalAccountInfo()) {
				var fields = ['email', 'title', 'firstName', 'middleName', 'lastName']
				_.each(fields, function(field) {
					this._secureField(field)
				}.bind(this))
				this.$('.user-profile-password, .user-profile-password-retype').remove()
				this.$el.addClass('is-not-editable')
			}
		}
		// when editing other users, the server will figure out if the user is editable
		else if (!this.model.get('editable') && !this.options.editable) {
			var fields = ['username', 'email', 'title', 'firstName', 'middleName', 'lastName']
			_.each(fields, function(field) {
				this._secureField(field)
			}.bind(this))
			this.$('.user-profile-password, .user-profile-password-retype').remove()
		}
	},

	setPasswordPlaceholders: function() {
		this.model.set({
			password: constants.PASSWORD_PLACEHOLDER_KEY,
			confirmPassword: constants.PASSWORD_PLACEHOLDER_KEY
		})
	},

	_secureField: function(name) {
		var el = this.$('[name=' + name + ']')
		if (el.length === 0) return

		var val = el.val()
		if (el[0].tagName.toLowerCase() === 'select') {
			el = selects.el(el)
		}

		var replacement = '<span class="value">' + _.escape(val) + '<span>'
		if (el.parent().hasClass('marked-field')) {
			el.parent().replaceWith(replacement)
		} else {
			el.replaceWith(replacement)
		}
	},

	_onImpersonate() {
		const titleKey = i18n.get('login.as', this.model.get('label'))
		const url = `api/admin/users/${this.model.id}/impersonate/validate`
		const error = xhr => errorHandler(xhr,{ showEmptyState: false, handler: () => {
			const {prelude, causes} = xhr.responseJSON
			if (prelude) {
				appDisplay.dialog(new ConfirmPrompt({
					confirmOnly: true,
					detailed: true,
					confirmLabel: 'common.close',
					titleKey,
					message: i18n.get(prelude),
					messageItems: _.map(causes, c => this.formatImpersonationIssue(c))
				}))
				return true
			}
		}})
		appDisplay.dialog($.get({url, error}), () => new ConfirmPrompt({
			confirmLabel: 'common.login',
			cancelLabel: 'common.cancel',
			titleKey,
			message: i18n.get('login.impersontate.confirm',
				highlight(this.model.get('label')), // New User
				highlight(user.label)), // Current User (impersonator)
			onConfirm: () => {
				return $.post(`api/admin/users/${this.model.id}/impersonate`)
					.then((data) => {
						// #28712 - normal triggers don't work here because impersonation
						// triggers a new route, views gets removed and events don't
						// fire properly resulting in this bug due to an extra dialog on the stack
						// so we must just pop it off here explicitly
						appDisplay.popDialog()

						user.load(data.user, user.model)
						user.assignState(data)
					})
			}
		}))
	},

	formatImpersonationIssue: function(cause) {
		let code = _.get(cause, 'codes[0]', null)
		if (!code.startsWith('impersonate.permissionDenied.datasets')) {
			// #46500 for the 2 messages that contain the org id, only highlight
			// the other args (that are not the org id)
			let messageArgs = cause.arguments
			if (code == 'impersonate.permissionDenied.missingViewOrg') {
				messageArgs[0] = highlight(messageArgs[0])
				return i18n.get(cause)
			} else if (code == 'impersonate.permissionDenied.missingOrgPermission') {
				messageArgs[0] = highlight(messageArgs[0])
				messageArgs[1] = highlight(messageArgs[1])
				return i18n.get(cause)
			} else {
				// all other non-dataset messages have all args highlighted
				return i18n.highlight(cause)
			}
		} else {
			let datasetType = 'dataset'
			let messageArgs = cause.arguments

			// gather dataset info from message args
			let datasetLabel = messageArgs[0]
			let datasetId = messageArgs[1]

			if (code.startsWith('impersonate.permissionDenied.datasets.records')
				|| code == 'impersonate.permissionDenied.datasets.permissionsFilter') {
				messageArgs[0] = this.buildErrorCauseLink(datasetLabel, datasetType, datasetId)
				if (! code.includes('.self')) {
					// gather principal info from message args
					const principalLabel = messageArgs[2]
					const domainTypeLabel = i18n.get(messageArgs[3]).toLowerCase()
					const principalId = messageArgs[4]
					const treeId = messageArgs[5]
					messageArgs[2] = this.buildErrorCauseLink(principalLabel, messageArgs[3], principalId, treeId)
					messageArgs[3] = domainTypeLabel
				}

			} else if (code == 'impersonate.permissionDenied.datasets'
				|| code == 'impersonate.permissionDenied.datasets.permissionsFilter.datasetOnly') {
				messageArgs[0] = this.buildErrorCauseLink(datasetLabel, datasetType, datasetId)

			} else if (code == 'impersonate.permissionDenied.datasets.permissions') {
				let permissionLabel = i18n.get(messageArgs[0])
				let datasetLabel = messageArgs[1]
				let datasetId = messageArgs[2]
				messageArgs[1] = this.buildErrorCauseLink(datasetLabel, datasetType, datasetId)
				messageArgs[0] = highlight(permissionLabel)
			}
		}
		return i18n.get(cause)
	},

	buildErrorCauseLink: function(label, domainTypeSingularCode, id, treeId) {
		let href = domainTypeSingularCode === 'dataset' ? '/' : '/admin/'
		if (domainTypeSingularCode === 'datasets.rollupTreeGroup') {
			href = `/admin/dataset-trees/${treeId}/rollup-tree-groups/${id}`
		} else { // user, group, dataset
			const domainType = i18n.get(domainTypeSingularCode) && i18n.get(domainTypeSingularCode).toLowerCase()
			href += `${domainType}s/${id}`
		}
		return `<a class="text-link sp-navigate is-inlined" href="${href}">` + _.escape(label) + `</a>`
	},

	cancel: function() {
		// set passwords back to placeholders
		this.setPasswordPlaceholders()

		// #55025 if there was no title or middleName on the original model,
		// then they were blank. cancelling changes should reflect this original
		// state.
		const data = _.extend({}, { title: '', middleName: '' },
			this.model.attributes)
		formHelper.writeToDom(data, this.el)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap(this.markedFields, 'remove')
	}

})

export default UserBasicsForm

UserBasicsForm.__filename__ = 'admin/principals/users/user-basics-form.js'
